import { useState } from 'react'
import { Button, CloseButton, Drawer, HStack, Input, Portal, VStack } from '@chakra-ui/react'

const COULDNT_REASONS = ['Nobody home', "Couldn't find it", 'Not safe to stop'] as const
/** Input length only (spec §7.3); the server validates the reason itself. */
const MAX_COULDNT_REASON = 200

/**
 * [CF-1 P3 §7.3] "Couldn't" at an action stop: an optional reason (a chip, or up to 200 characters of
 * free text). "Skip reason" records it with no reason. The chosen chip wins over typed text only when
 * nothing is typed.
 */
export function CouldntReasonSheet({ open, onClose, onSubmit }: {
  open: boolean; onClose: () => void; onSubmit: (reason: string | null) => void
}) {
  const [chip, setChip] = useState<string | null>(null)
  const [text, setText] = useState('')
  const reset = () => { setChip(null); setText('') }
  const typed = text.trim()
  const reason = typed || chip
  const submit = (r: string | null) => { onSubmit(r); reset() }
  return (
    <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => { if (!e.open) { reset(); onClose() } }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header><Drawer.Title>What happened?</Drawer.Title></Drawer.Header>
            <Drawer.Body>
              <VStack align="stretch" gap={4} pb={4}>
                <HStack gap={2} wrap="wrap">
                  {COULDNT_REASONS.map(r => (
                    <Button key={r} minH="44px" borderRadius="full" variant="outline" aria-pressed={chip === r}
                      borderColor={chip === r ? 'accent' : 'border'} bg={chip === r ? 'accent.soft' : 'surface'}
                      color={chip === r ? 'accent' : 'fg'} onClick={() => setChip(c => (c === r ? null : r))}>
                      {r}
                    </Button>
                  ))}
                </HStack>
                <Input aria-label="Something else?" placeholder="Something else? (optional)" minH="44px"
                  maxLength={MAX_COULDNT_REASON} value={text} onChange={(e) => setText(e.target.value)} />
                <Button minH="52px" colorPalette="brand" disabled={!reason} onClick={() => submit(reason)}>Record</Button>
                <Button minH="44px" variant="ghost" onClick={() => submit(null)}>Skip reason</Button>
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
